import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import ConditionOverlay from "./ConditionOverlay";

import { useConsent } from "../../../helpers/useConsent";

type ExtraMeta = Record<string, any> | Array<{ key: string; value: any }>;

type PayPalCheckoutProductProps = {
  productId: number;
  amountCents: number;
  extraMetadata?: ExtraMeta;
  paypalOptions: {
    client_id: string;
    createOrderUrl: string;
    completeCheckoutUrl: string;
    paymentCaptureUrl: string;
    currency?: string;
    locale?: string;
  };
  isDelivery?: boolean;
};

type PayPalIntegrationMode = "provider" | "external";

function normalizePayPalLocale(raw?: string): string {
  const fallback = "en_US";
  if (!raw) return fallback;
  const s = raw.replace("-", "_");
  if (/^[a-z]{2}_[A-Z]{2}$/.test(s)) return s;
  const lang = raw.split(/[-_]/)[0]?.toLowerCase() || "en";
  const map: Record<string, string> = {
    en: "en_US",
    it: "it_IT",
    fr: "fr_FR",
    de: "de_DE",
    es: "es_ES",
    pt: "pt_PT",
    nl: "nl_NL",
    pl: "pl_PL",
  };
  return map[lang] || fallback;
}


/**
 * Ritorna true se una SDK PayPal è già inizializzata (Buttons presente).
 */
function hasWindowPayPal(): boolean {
  if (typeof window === "undefined") return false;
  const wp = (window as any).paypal;
  return !!wp && typeof wp.Buttons === "function";
}

/**
 * Attende che window.paypal.Buttons sia disponibile.
 */
function waitForPayPalButtons(
  intervalMs = 50,
  maxMs = 20000
): Promise<void> {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    (function tick() {
      if (hasWindowPayPal()) {
        return resolve();
      }
      if (Date.now() - start > maxMs) {
        return reject(
          new Error("PayPal SDK Buttons non disponibile (timeout)")
        );
      }
      setTimeout(tick, intervalMs);
    })();
  });
}

function getRestNonce(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as any;

  const cfg = w.__GARFY_CHECKOUT_EXPRESS__;
  const nonce =
    cfg?.nonce ||
    w?.wpApiSettings?.nonce || // classico di WP
    w?.wprest_nonce;           // tuo fallback storico

  if (!nonce) {
    console.warn(
      "[CheckoutExpress] Nessun nonce REST trovato (GARFY/ wpApiSettings / wprest_nonce). La REST sarà anonima."
    );
  }

  return nonce;
}

/**
 * Factory per la funzione createOrder riusabile sia in modalità provider
 * sia in modalità external.
 */
function makeCreateOrder({
  metaRef,
  isDelivery,
  providerCurrency,
  productId,
  createOrderUrl,
}: {
  metaRef: React.MutableRefObject<{
    amount: number;
    meta: Record<string, any>;
  }>;
  isDelivery: boolean;
  providerCurrency: string;
  productId: number;
  createOrderUrl: string;
}) {
  return async () => {
 
 const headers: Record<string, string> = {
  "Content-Type": "application/json",
};

    const wpNonce = getRestNonce();
    if (wpNonce) {
      headers["X-WP-Nonce"] = wpNonce;
      console.log("[CheckoutExpress] wpNonce usato in createOrder:", wpNonce);
    } else {
      console.warn(
        "[CheckoutExpress] Nessun nonce trovato in __GARFY_CHECKOUT_EXPRESS__, la REST sarà anonima"
      );
    }

    const amt = metaRef.current.amount;
    const meta = metaRef.current.meta;

    if (!Number.isFinite(amt) || amt <= 0) {
      throw new Error("Invalid amountCents");
    }

    const res = await fetch(
      `${createOrderUrl}?forceHideShipping=${
        isDelivery ? 0 : 1
      }`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          amount: amt,
          currency: providerCurrency,
          metadata: meta,
          product_id: productId,
          shippingPreference: isDelivery
            ? "SHIPPING"
            : "NO_SHIPPING",
        }),
      }
    );

    const txt = await res.text();

    if (!res.ok) {
      let payload;
      try {
        payload = JSON.parse(txt);
      } catch {
        throw new Error(`createOrder ${res.status}: ${txt}`);
      }

      if (payload?.redirect) {
        window.location.href = payload.redirect;
        return;
      }

      throw new Error(
        payload?.error || `createOrder ${res.status}: ${txt}`
      );
    }

    const json = JSON.parse(txt);
    return json.id || json.idOrder;
  };
}

/** --- Bottoni (modalità provider: usa PayPalScriptProvider) --- **/
function PayPalButtonsWhenReady(props: {
  amountCents: number;
  providerCurrency: string;
  isDelivery: boolean;
  productId: number;
  createOrderUrl: string;
  completeCheckoutUrl: string;
  paymentCaptureUrl: string;
  metaRef: React.MutableRefObject<{
    amount: number;
    meta: Record<string, any>;
  }>;
  reRenderKeys: any[];
  onStartRedirect: () => void;
  onError: (e: any) => void;
}) {
  const [{ isResolved }] = usePayPalScriptReducer();

  const {
    amountCents,
    providerCurrency,
    isDelivery,
    productId,
    createOrderUrl,
    completeCheckoutUrl,
    paymentCaptureUrl,
    metaRef,
    reRenderKeys,
    onStartRedirect,
    onError,
  } = props;

  const createOrder = useMemo(
    () =>
      makeCreateOrder({
        metaRef,
        isDelivery,
        providerCurrency,
        productId,
        createOrderUrl,
      }),
    [metaRef, isDelivery, providerCurrency, productId, createOrderUrl]
  );

 


  if (!isResolved) {
    // Placeholder skeleton
    return (
      <div
        style={{ height: 42, width: 240 }}
        className="paypal-skeleton"
      />
    );
  }
 
  return (
    <PayPalButtons
      style={{
        layout: "horizontal",
        disableMaxWidth: true,
        label: "paypal",
        tagline: false,
        height: 42,
      }}
      disabled={!Number.isFinite(amountCents) || amountCents <= 0}
      forceReRender={reRenderKeys}
      createOrder={createOrder}
      onApprove={(data) => {
        onStartRedirect();

        const form = document.createElement("form");
        form.target = "_top";
        form.method = "POST";
        form.action = isDelivery ? completeCheckoutUrl : paymentCaptureUrl;

       
        // paymentData
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "paymentData";
        (data as any).delivery = isDelivery ? "delivery" : "pickup";
        input.value = JSON.stringify(data);
        form.appendChild(input);

        // _wpnonce per autenticare la REST con i cookie
        const wpNonce = getRestNonce();
        if (wpNonce) {
          const nonceInput = document.createElement("input");
          nonceInput.type = "hidden";
          nonceInput.name = "_wpnonce";
          nonceInput.value = wpNonce;
          form.appendChild(nonceInput);
          console.log("[CheckoutExpress] use _wpnonce in form complete_checkout");
        }  

        document.body.appendChild(form);
        form.submit();
      }}

      onError={onError}
    />
  );
}

/** --- Bottoni (modalità external: usa window.paypal.Buttons) --- **/
function PayPalButtonsExternal(props: {
  amountCents: number;
  providerCurrency: string;
  isDelivery: boolean;
  productId: number;
  createOrderUrl: string;
  completeCheckoutUrl: string;
  paymentCaptureUrl: string;
  metaRef: React.MutableRefObject<{
    amount: number;
    meta: Record<string, any>;
  }>;
  reRenderKeys: any[];
  onStartRedirect: () => void;
  onError: (e: any) => void;
}) {
  const {
    amountCents,
    providerCurrency,
    isDelivery,
    productId,
    createOrderUrl,
    completeCheckoutUrl,
    paymentCaptureUrl,
    metaRef,
    reRenderKeys,
    onStartRedirect,
    onError,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let buttons: any | null = null;
    let cancelled = false;

    async function init() {
      try {
        await waitForPayPalButtons();

        if (!containerRef.current || cancelled) return;

        const createOrder = makeCreateOrder({
          metaRef,
          isDelivery,
          providerCurrency,
          productId,
          createOrderUrl,
        });

        const wp = (window as any).paypal;
        buttons = wp.Buttons({
          style: {
            layout: "horizontal",
            disableMaxWidth: true,
            label: "paypal",
            tagline: false,
            height: 42,
          },
          disabled: !Number.isFinite(amountCents) || amountCents <= 0,
          createOrder,
          onApprove: (data: any) => {
            try {
              onStartRedirect();

              const form = document.createElement("form");
              form.target = "_top";
              form.method = "POST";
              form.action = isDelivery ? completeCheckoutUrl : paymentCaptureUrl;

              // paymentData
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = "paymentData";
              (data as any).delivery = isDelivery ? "delivery" : "pickup";
              input.value = JSON.stringify(data);
              form.appendChild(input);

              // _wpnonce per cookie-auth REST
              const wpNonce = getRestNonce();
              if (wpNonce) {
                const nonceInput = document.createElement("input");
                nonceInput.type = "hidden";
                nonceInput.name = "_wpnonce";
                nonceInput.value = wpNonce;
                form.appendChild(nonceInput);
                console.log("[CheckoutExpress] use _wpnonce in form complete_checkout (external)");
              }
              document.body.appendChild(form);
              form.submit();
            } catch (e) {
              onError(e);
            }
          },

          onError: onError,
        });

        await buttons.render(containerRef.current);
      } catch (e) {
        console.error("[PayPalButtonsExternal] error", e);
        onError(e);
      }
    }

    init();

    return () => {
      cancelled = true;
      try {
        if (buttons) {
          buttons.close();
        }
      } catch {
        // ignore
      }
    };
    // reRenderKeys usato solo per triggerare re-init se cambia amount/currency
  }, [
    amountCents,
    providerCurrency,
    isDelivery,
    productId,
    createOrderUrl,
    completeCheckoutUrl,
    paymentCaptureUrl,
    metaRef,
    onStartRedirect,
    onError,
    JSON.stringify(reRenderKeys),
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: 50,
        minWidth: 220,
      }}
    />
  );
}

/** --- Wrapper principale --- **/
const PayPalCheckoutProduct: React.FC<PayPalCheckoutProductProps> = ({
  productId,
  amountCents,
  extraMetadata,
  paypalOptions,
  isDelivery,
}) => {
  const { isAccepted, showError, setShowError, handleCheckboxChange } =
    useConsent("paypal");

  const [overlayVisible, setOverlayVisible] = useState(false);
  const [firstClick, setFirstClick] = useState(false);
  const [showRedirectLoading, setShowRedirectLoading] =
    useState(false);

  const paypalBtnRef = useRef<HTMLDivElement | null>(null);
  const latestRef = useRef<{
    amount: number;
    meta: Record<string, any>;
  }>({
    amount: 0,
    meta: {},
  });

  const [mode, setMode] = useState<PayPalIntegrationMode | null>(
    null
  );
  const [modeError, setModeError] = useState<string | null>(null);

  const metaObj = useMemo(() => {
    if (Array.isArray(extraMetadata)) {
      return extraMetadata.reduce(
        (acc, { key, value }) => {
          acc[key] = value;
          return acc;
        },
        {} as Record<string, any>
      );
    }
    return extraMetadata || {};
  }, [extraMetadata]);

  useEffect(() => {
    latestRef.current = {
      amount: typeof amountCents === "number" ? amountCents : 0,
      meta: metaObj,
    };
  }, [amountCents, metaObj]);

  useEffect(() => {
    if (!isAccepted) return;
    if (!firstClick) setFirstClick(true);
    paypalBtnRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [isAccepted, firstClick]);

  useEffect(() => {
    if (!isAccepted) return;
    if (overlayVisible) setOverlayVisible(false);
    if (showError) setShowError(false);
  }, [isAccepted, overlayVisible, showError, setShowError]);

  const handleContinue = () => {
    if (!overlayVisible) setOverlayVisible(true);
    if (!firstClick) setFirstClick(true);
    if (!isAccepted) setShowError(true);
  };

  const normalizedLocale = useMemo(() => {
    const raw =
      paypalOptions.locale ||
      (typeof document !== "undefined"
        ? document.documentElement.lang
        : "");
    return normalizePayPalLocale(raw);
  }, [paypalOptions.locale]);

  const sdkOptions = useMemo(
    () => ({
      "client-id": paypalOptions.client_id,
      currency: (paypalOptions.currency || "EUR").toUpperCase(),
      intent: "capture" as const,
      components: "buttons",
      locale: normalizedLocale,
      // IMPORTANTE: no data-namespace → condividiamo window.paypal
    }),
    [paypalOptions.client_id, paypalOptions.currency, normalizedLocale]
  );

  const reRenderKeys = useMemo(
    () => [sdkOptions.currency, amountCents],
    [sdkOptions.currency, amountCents]
  );

  if (!paypalOptions.client_id) {
    console.error("[PayPal] client_id mancante");
    return null;
  }

  /**
   * Detection automatica della modalità (provider vs external)
   * basata su presenza di script PayPal e window.paypal.
   */
  /**
   * Detection automatica della modalità (provider vs external)
   * basata SOLO sulla presenza di script PayPal o window.paypal.
   *
   * Regola:
   * - se troviamo QUALSIASI script PayPal o window.paypal.Buttons entro la finestra → external
   * - se non troviamo nulla → provider
   */
  useEffect(() => {
    let cancelled = false;

    async function detectMode() {
      try {
        const detectionWindowMs = 700;
        const stepMs = 70;
        const start = Date.now();

        let chosen: PayPalIntegrationMode | null = null;

        while (!chosen && Date.now() - start < detectionWindowMs) {
          const hasWp = hasWindowPayPal();

          let hasAnyScript = false;
          if (typeof document !== "undefined") {
            const s = document.querySelector(
              'script[src*="paypal.com/sdk/js"]'
            );
            hasAnyScript = !!s;
          }

          // Se c'è già una SDK inizializzata oppure QUALSIASI script PayPal
          if (hasWp || hasAnyScript) {
            chosen = "external";
            console.log("[PayPalCheckout] mode decision: external", {
              hasWp,
              hasAnyScript,
            });
            break;
          }

          // Nessun segnale ancora → aspettiamo un altro giro
          await new Promise((r) => setTimeout(r, stepMs));
        }

        if (!chosen) {
          // Nessuno script e nessun window.paypal entro la finestra → assumiamo provider
          chosen = "provider";
          console.log(
            "[PayPalCheckout] mode decision: provider (no PayPal SDK detected)"
          );
        }

        if (!cancelled) {
          setMode(chosen);
          setModeError(null);
        }
      } catch (e: any) {
        console.error("[PayPalCheckout] mode detection error", e);
        if (!cancelled) {
          setMode(null);
          setModeError(
            e?.message ||
              "Impossibile inizializzare PayPal in questa pagina."
          );
        }
      }
    }

    detectMode();

    return () => {
      cancelled = true;
    };
  }, []);


  if (modeError) {
    return (
      <div className="mb-3" id="paypal-checkout-product">
        <div data-pp-error>{modeError}</div>
      </div>
    );
  }

  // mentre decidiamo la modalità, mostriamo solo placeholder
  if (mode === null) {
    return (
      <div className="mb-3" id="paypal-checkout-product">
        <div
          style={{ minHeight: 50, minWidth: 220 }}
          className="paypal-skeleton"
        />
      </div>
    );
  }

  return (
    <>
      <div className="mb-3" id="paypal-checkout-product">
        <ConditionOverlay
          visible={overlayVisible && !isAccepted}
          showError={showError}
          checked={isAccepted}
          onCheckboxChange={handleCheckboxChange}
          idPrefix="paypal"
        />
        <div
          ref={paypalBtnRef}
          style={{
            position: "relative",
            minHeight: 50,
            minWidth: 220,
          }}
        >
          {mode === "provider" ? (
            <PayPalScriptProvider
              options={sdkOptions}
              deferLoading={false}
            >
              <PayPalButtonsWhenReady
                amountCents={amountCents}
                providerCurrency={sdkOptions.currency}
                isDelivery={isDelivery}
                productId={productId}
                createOrderUrl={paypalOptions.createOrderUrl}
                completeCheckoutUrl={paypalOptions.completeCheckoutUrl}
                paymentCaptureUrl={paypalOptions.paymentCaptureUrl}
                metaRef={latestRef}
                reRenderKeys={reRenderKeys}
                onStartRedirect={() => setShowRedirectLoading(true)}
                onError={(err) => {
                  console.error("PayPal error:", err);
                  setShowRedirectLoading(false);
                  setShowError(true);
                }}
              />
            </PayPalScriptProvider>
          ) : (
            <PayPalButtonsExternal
              amountCents={amountCents}
              providerCurrency={sdkOptions.currency}
              isDelivery={isDelivery}
              productId={productId}
              createOrderUrl={paypalOptions.createOrderUrl}
              completeCheckoutUrl={paypalOptions.completeCheckoutUrl}
              paymentCaptureUrl={paypalOptions.paymentCaptureUrl}
              metaRef={latestRef}
              reRenderKeys={reRenderKeys}
              onStartRedirect={() => setShowRedirectLoading(true)}
              onError={(err) => {
                console.error("PayPal error (external):", err);
                setShowRedirectLoading(false);
                setShowError(true);
              }}
            />
          )}

          {!isAccepted && (
            <div
              className="acceptance-overlay"
              onClick={handleContinue}
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 9999,
                backgroundColor: firstClick
                  ? "rgba(252,252,252,0.5)"
                  : "transparent",
                display: "block",
                cursor: "pointer",
              }}
            />
          )}
        </div>
      </div>

      {showRedirectLoading && (
        <div
          role="alert"
          aria-busy="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.75)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="spinner-border text-light" />
        </div>
      )}
    </>
  );
};

export default PayPalCheckoutProduct;
